import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoTooltip from 'components/InfoTooltip';
import { T2B_FEE_CAP, T2B_FEE_FLOOR } from 'utils/bookingPricing';

// The "i" that sits next to the There2B service fee line and explains, on
// hover, that the 5% is bounded: "Min 1 € | Max 50 €".
//
// The bounds come from utils/bookingPricing so this can never disagree with
// the amount charged — do not hardcode them here. Whole-euro bounds print
// without cents, which is how the copy reads in the design.
const fmtBound = (n, lang) =>
  new Intl.NumberFormat(lang || 'pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);

const FeeBoundsTooltip = ({ className = '' }) => {
  const { t, i18n } = useTranslation();

  // Either bound can be switched off (the API reads both from env). With
  // neither in force there is nothing to explain, so drop the icon entirely.
  const hasFloor = T2B_FEE_FLOOR > 0;
  const hasCap = Number.isFinite(T2B_FEE_CAP);
  if (!hasFloor && !hasCap) return null;

  const lang = i18n?.language;
  const min = fmtBound(T2B_FEE_FLOOR, lang);
  const max = fmtBound(T2B_FEE_CAP, lang);

  let text;
  if (hasFloor && hasCap) {
    text = t('booking.fee_bounds', 'Min {{min}} | Max {{max}}', { min, max });
  } else if (hasFloor) {
    text = t('booking.fee_bounds_min_only', 'Min {{min}}', { min });
  } else {
    text = t('booking.fee_bounds_max_only', 'Max {{max}}', { max });
  }

  return (
    <InfoTooltip
      className={className}
      label={t('booking.fee_bounds_label', 'Service fee limits')}
    >
      {text}
    </InfoTooltip>
  );
};

export default FeeBoundsTooltip;
